import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';

import { PremierText } from '@components/text';
import { formatDuration } from '@utils/index';
import { SKEW_ANGLE } from '@utils/constants';

const Container = styled.div<{ $backgroundFill: string }>`
  display: flex;
  flex-direction: column;
  background-color: ${({ $backgroundFill }) => $backgroundFill};
  transform: skew(-${SKEW_ANGLE}deg);
`;

const Content = styled(PremierText)`
  padding: 4px 8px;
  font-size: ${({ theme }) => theme.fonts.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.white};
  transform: skew(${SKEW_ANGLE}deg);
`;

type Props = {
  startTime: string;
  backgroundFill: string;
};

const Countdown: FC<Props> = ({ startTime, backgroundFill }) => {
  const calculateTimeLeft = useCallback(() => {
    if (!startTime) {
      return 0;
    }
    const targetTime = new Date(startTime).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((targetTime - now) / 1000));
  }, [startTime]);

  const initialTimeLeft = useMemo(
    () => calculateTimeLeft(),
    [calculateTimeLeft],
  );
  const [timeLeft, setTimeLeft] = useState<number>(initialTimeLeft);

  useEffect(() => {
    setTimeLeft(initialTimeLeft);
  }, [initialTimeLeft]);

  useEffect(() => {
    if (timeLeft <= 0) {
      return;
    }

    const interval = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [timeLeft, calculateTimeLeft]);

  if (!startTime) {
    return null;
  }

  return (
    <Container $backgroundFill={backgroundFill}>
      <Content>{timeLeft <= 0 ? '00:00:00' : formatDuration(timeLeft)}</Content>
    </Container>
  );
};

export default Countdown;
